import imageCompression, { Options } from 'browser-image-compression';
import { convertHeicFileToBlob } from '../lib/convertHeic';

/**
 * Thrown when an image cannot be compressed below the maximum upload size even
 * after exhausting the dimension ladder. Lets callers distinguish "couldn't
 * shrink it" from a user-triggered abort.
 */
export class ImageTooLargeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageTooLargeError';
  }
}

export interface CompressImageOptions {
  signal?: AbortSignal;
  onProgress?: (progress: number) => void;
}

export class ImageUtil {
  // Hard reject cap: a photo that cannot be shrunk below this is refused.
  static readonly defaultMaxImageSizeMegabytes = 90;
  // Best-effort UPLOAD target: ordinary phone photos are downscaled toward this
  // (decoupled from the reject cap) so we do not cache/upload full-resolution
  // multi-MB originals over poor mine-site connectivity. 3 MB at <=3072px keeps
  // ample wear detail (~9 MP) while cutting a typical photo ~5-10x.
  static readonly defaultUploadTargetMegabytes = 3;
  static readonly defaultMaxImageDimension = 4096;
  static readonly defaultCompressionInitialQuality = 0.85;

  // Descending maxWidthOrHeight ladder. The library already iterates on quality
  // toward the target size; dimensions are the stronger lever, so we only step
  // down a rung when the previous one still exceeds the target. Bytes scale
  // ~quadratically with dimension, so rung 1-2 essentially always wins. Starts at
  // 3072 (not 4096) so normal photos are meaningfully downscaled for upload.
  static readonly compressionDimensionLadder = [3072, 2048, 1536, 1024];

  // Small, low-quality JPEG used only for in-app grid tiles (never uploaded).
  static readonly thumbnailMaxDimension = 256;
  static readonly thumbnailQuality = 0.5;

  static getMaxImageSizeMegabytes(): number {
    const parsedMaxImageSize = Number(import.meta.env.VITE_MAX_IMAGE_SIZE_MB);
    return Number.isFinite(parsedMaxImageSize) && parsedMaxImageSize > 0
      ? parsedMaxImageSize
      : ImageUtil.defaultMaxImageSizeMegabytes;
  }

  static getMaxImageSizeBytes(): number {
    return ImageUtil.getMaxImageSizeMegabytes() * 1024 * 1024;
  }

  static isImageAboveMaxSize(image: Blob): boolean {
    return image.size > ImageUtil.getMaxImageSizeBytes();
  }

  static getUploadTargetMegabytes(): number {
    const parsed = Number(import.meta.env.VITE_IMAGE_UPLOAD_TARGET_MB);
    return Number.isFinite(parsed) && parsed > 0
      ? parsed
      : ImageUtil.defaultUploadTargetMegabytes;
  }

  // Best-effort upload target in bytes, never larger than the hard reject cap.
  static getUploadTargetBytes(): number {
    return Math.min(
      ImageUtil.getUploadTargetMegabytes() * 1024 * 1024,
      ImageUtil.getMaxImageSizeBytes(),
    );
  }

  // Compress anything above the upload target - not just above the 90 MB reject cap -
  // so ordinary multi-MB phone photos are downscaled before caching and upload.
  static shouldCompressForUpload(image: Blob): boolean {
    return image.size > ImageUtil.getUploadTargetBytes();
  }

  static toJpegFileName(fileName: string): string {
    const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
    return `${nameWithoutExtension || 'image'}.jpeg`;
  }

  /**
   * Compresses an image to a JPEG that fits under the max upload size by
   * stepping down a maxWidthOrHeight ladder until the result fits. Throws
   * {@link ImageTooLargeError} if it still exceeds the cap after the last rung.
   */
  static async compressImageForUpload(
    file: File,
    opts: CompressImageOptions = {},
  ): Promise<File> {
    const targetBytes = ImageUtil.getUploadTargetBytes();
    const maxBytes = ImageUtil.getMaxImageSizeBytes();
    let smallest: File | null = null;

    for (const maxWidthOrHeight of ImageUtil.compressionDimensionLadder) {
      if (opts.signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      const options: Options = {
        maxSizeMB: ImageUtil.getUploadTargetMegabytes(),
        maxWidthOrHeight,
        // The worker path imports the library from a CDN by default, which is not reliable offline.
        useWebWorker: false,
        fileType: 'image/jpeg',
        initialQuality: ImageUtil.defaultCompressionInitialQuality,
        signal: opts.signal,
        onProgress: opts.onProgress,
      };

      const compressedFile = await imageCompression(file, options);
      const result = new File(
        [compressedFile],
        ImageUtil.toJpegFileName(file.name),
        {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        },
      );
      console.log(
        `compressed image size at ${maxWidthOrHeight}px: ${result.size}`,
      );

      // Reached the best-effort target — smallest acceptable size, stop early.
      if (result.size <= targetBytes) {
        return result;
      }
      if (!smallest || result.size < smallest.size) {
        smallest = result;
      }
    }

    // Couldn't reach the target. As long as the smallest rung is still under the
    // hard reject cap, upload it anyway - never reject a photo the cap would have
    // accepted. Only give up when even the smallest exceeds the cap.
    if (smallest && smallest.size <= maxBytes) {
      return smallest;
    }

    throw new ImageTooLargeError(
      `Unable to compress "${file.name}" below ${ImageUtil.getMaxImageSizeMegabytes()} MB` +
        (smallest
          ? ` (smallest achieved: ${(smallest.size / (1024 * 1024)).toFixed(1)} MB)`
          : ''),
    );
  }

  static async prepareImageForUpload(
    file: File,
    opts: CompressImageOptions = {},
  ): Promise<File> {
    const sourceFile = (await ImageUtil.isHEICImage(file))
      ? await ImageUtil.convertHEICToJPEG(file)
      : file;

    if (opts.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    return ImageUtil.shouldCompressForUpload(sourceFile)
      ? await ImageUtil.compressImageForUpload(sourceFile, opts)
      : sourceFile;
  }

  /**
   * Produces a small low-quality JPEG thumbnail for in-app grid display only
   * (never uploaded). Converts HEIC first so the compressor can read it.
   */
  static async createThumbnail(source: Blob): Promise<Blob> {
    let file =
      source instanceof File
        ? source
        : new File([source], 'image', { type: source.type || 'image/jpeg' });

    if (await ImageUtil.isHEICImage(file)) {
      file = await ImageUtil.convertHEICToJPEG(file);
    }

    const options: Options = {
      maxSizeMB: 0.2,
      maxWidthOrHeight: ImageUtil.thumbnailMaxDimension,
      useWebWorker: false,
      fileType: 'image/jpeg',
      initialQuality: ImageUtil.thumbnailQuality,
    };

    return imageCompression(file, options);
  }

  /**
   * Reads the first N bytes of a File and returns them as a Uint8Array.
   */
  static async getFileHeader(
    file: File,
    length: number = 6,
  ): Promise<Uint8Array> {
    const blob = file.slice(0, length);
    const arrayBuffer = await blob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  }

  /**
   * Checks the file header for known image types (HEIC, JPG/JPEG, GIF).
   * Returns true if the header matches a known type.
   */
  static async isValidImageHeader(file: File): Promise<boolean> {
    const header = await ImageUtil.getFileHeader(file);

    // JPG/JPEG: FF D8 FF
    if (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff) {
      return true;
    }

    // GIF: GIF87a or GIF89a
    if (
      header[0] === 0x47 && // 'G'
      header[1] === 0x49 && // 'I'
      header[2] === 0x46 && // 'F'
      header[3] === 0x38 &&
      (header[4] === 0x37 || header[4] === 0x39) &&
      header[5] === 0x61
    ) {
      return true;
    }

    // HEIC: 'ftypheic' or 'ftypheix' at offset 4
    if (await this.isHEICImage(file)) {
      return true;
    }

    return false;
  }

  static async isHEICImage(file: File): Promise<boolean> {
    const header = await ImageUtil.getFileHeader(file, 12); // Read first 12 bytes

    // Check for 'ftypheic' or 'ftypheix' at offset 4
    return (
      (header.length >= 12 &&
        header[8] === 0x68 && // 'h'
        header[9] === 0x65 && // 'e'
        header[10] === 0x69 && // 'i'
        (header[11] === 0x63 || header[11] === 0x78)) || // 'c' or 'x'
      file.name.toLowerCase().endsWith('.heic') ||
      file.name.toLowerCase().endsWith('.heif') ||
      file.type === 'image/heic' ||
      file.type === 'image/heif'
    );
  }

  static async convertHEICToJPEG(file: File): Promise<File> {
    const blob = await convertHeicFileToBlob(file, {
      format: 'image/jpeg',
      quality: 0.9,
    });

    const newFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpeg'), {
      type: 'image/jpeg',
    });

    return newFile;
  }

  static async convertHEICToJPEGBlob(file: File): Promise<Blob> {
    const blob = await convertHeicFileToBlob(file, {
      format: 'image/jpeg',
      quality: 0.9,
    });

    return blob;
  }
}
